import React, { useState, useEffect } from "react";
import { Layout } from "./components/dashboard/layout";
import {
  Box, Typography, Chip, Button,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";

const BASE_URL = "https://raptornet.aischennai.org/screening-manager";

const CATEGORIES = [
  { key: "student",    label: "Students",    source: "PowerSchool" },
  { key: "parent",     label: "Parents",     source: "PowerSchool" },
  { key: "staff",      label: "Staff",       source: "PowerSchool" },
  { key: "adhoc",      label: "Ad hoc",      source: "RaptorNet"   },
  { key: "jc_student", label: "JC Students", source: null          },
];

const num = (v) => (v === null || v === undefined ? null : Number(v));

const getStatus = (r) => {
  if (num(r.source_count) === null) return { label: "COSEC only", color: "default" };
  const clean = [r.missing, r.inactive, r.extra].every((v) => num(v) === 0);
  return clean ? { label: "MATCH", color: "success" } : { label: "MISMATCH", color: "warning" };
};

const diffCount = (r) =>
  (num(r.missing) || 0) + (num(r.inactive) || 0) + (num(r.extra) || 0);

const ActiveUsers = () => {
  const [summary, setSummary]   = useState({});
  const [runAt, setRunAt]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [selected, setSelected] = useState(null);
  const [diffs, setDiffs]       = useState([]);
  const [diffLoading, setDiffLoading] = useState(false);

  useEffect(() => {
    fetch(`${BASE_URL}/gate-api/activeUsers.php?action=summary`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) throw new Error(data.message || "Failed to load");
        const byCat = {};
        (data.data || []).forEach((r) => { byCat[r.category] = r; });
        setSummary(byCat);
        setRunAt(data.run_at);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const openCategory = (cat) => {
    setSelected(cat);
    setDiffs([]);
    setDiffLoading(true);
    fetch(`${BASE_URL}/gate-api/activeUsers.php?action=diff&category=${cat.key}`)
      .then((res) => res.json())
      .then((data) => setDiffs(data.data || []))
      .catch((err) => console.error("Error fetching differences:", err))
      .finally(() => setDiffLoading(false));
  };

  const downloadCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["ID", "Issue", "Source name", "COSEC name", "COSEC status", "Reason"];
    const lines = diffs.map((d) =>
      [d.ps_code, d.issue, d.source_name, d.cosec_name, d.cosec_status, d.reason].map(esc).join(",")
    );
    const blob = new Blob(["\uFEFF" + [header.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `active_users_${selected.key}_differences.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <Box sx={{ p: 4, maxWidth: 1000, margin: "0 auto" }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600, color: "#2E3192" }}>
          Active Users
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {runAt
            ? `Last updated: ${new Date(runAt.replace(" ", "T")).toLocaleString()}`
            : loading ? "Loading..." : "No data yet"}
        </Typography>

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {/* Category cards */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 2 }}>
          {CATEGORIES.map((cat) => {
            const r = summary[cat.key];
            if (!r) return null;
            const status = getStatus(r);
            const hasDiffs = diffCount(r) > 0;
            const isSelected = selected && selected.key === cat.key;
            return (
              <Box
                key={cat.key}
                onClick={hasDiffs ? () => openCategory(cat) : undefined}
                sx={{
                  background: "#fff",
                  borderRadius: "16px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  p: 3,
                  cursor: hasDiffs ? "pointer" : "default",
                  border: isSelected ? "2px solid #2E3192" : "2px solid transparent",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" fontWeight={600} color="#1a1a2e">{cat.label}</Typography>
                  <Chip size="small" label={status.label} color={status.color} />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-around", background: "#f0f4ff", borderRadius: "12px", p: 2 }}>
                  {cat.source && (
                    <Box textAlign="center">
                      <Typography variant="caption" color="text.secondary">{cat.source}</Typography>
                      <Typography variant="h4" fontWeight={700} color="#2E3192">{num(r.source_count)}</Typography>
                    </Box>
                  )}
                  <Box textAlign="center">
                    <Typography variant="caption" color="text.secondary">COSEC</Typography>
                    <Typography variant="h4" fontWeight={700} color="#2E3192">{num(r.cosec_count)}</Typography>
                  </Box>
                </Box>

                {cat.source && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                    Missing {num(r.missing)} · Inactive {num(r.inactive)} · Extra {num(r.extra)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Differences */}
        {selected && (
          <Box sx={{ mt: 4, background: "#fff", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={600} color="#2E3192">
                {selected.label} — differences ({diffs.length})
              </Typography>
              <Button variant="outlined" size="small" onClick={downloadCsv} disabled={!diffs.length}>
                Download CSV
              </Button>
            </Box>

            {diffLoading ? (
              <Typography color="text.secondary">Loading...</Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><b>ID</b></TableCell>
                      <TableCell><b>Issue</b></TableCell>
                      <TableCell><b>{selected.source || "Source"} name</b></TableCell>
                      <TableCell><b>COSEC name</b></TableCell>
                      <TableCell><b>COSEC status</b></TableCell>
                      <TableCell><b>Reason</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diffs.map((d, i) => (
                      <TableRow key={`${d.ps_code}-${i}`}>
                        <TableCell sx={{ fontFamily: "monospace" }}>{d.ps_code}</TableCell>
                        <TableCell>{d.issue}</TableCell>
                        <TableCell>{d.source_name}</TableCell>
                        <TableCell>{d.cosec_name}</TableCell>
                        <TableCell>{d.cosec_status}</TableCell>
                        <TableCell>
                          {d.reason ? <Chip size="small" label={d.reason} /> : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default ActiveUsers;
